import type { StringMap } from 'corgi-types';

import { RESULTS_HEADING_ID } from '../../results-heading/constants';
import { EventBusEvent } from '../enums/EventBus';
import eventBus from '../event-bus/EventBus';
import { isClient } from './isClient';

export type QueryParams = StringMap<string | number | boolean>;

const PATH_PREFIX_BLACKLIST = ['/nl', '/de'];

// Currently we have different definitions of multi-language regions URLS.
// For some cases even if language is a default we need to include path prefix,
// for other cases we need to remove them.
// Locale provides currently always adds path prefix for multi-languages regions.
// This method removes redundant default languages URLs prefixing.
export const getUrlPrefix = (pathPrefix: string, isCurrent?: boolean) =>
  isCurrent ? '#' : PATH_PREFIX_BLACKLIST.includes(pathPrefix) ? '/' : `${pathPrefix}/`;

export const getCurrentUrl = () => (isClient() ? window.location.href : '');

export const getLocationOrigin = () => (isClient() ? window.location.origin : '');

export const getLocationPathName = () => (isClient() ? window.location.pathname : '');

export const getUrlWithQueryParams = (values: QueryParams, baseUrl?: string) => {
  const urlToParse = baseUrl || getCurrentUrl();
  if (!urlToParse) {
    return '';
  }
  const url = new URL(urlToParse);

  Object.keys(values).forEach(key => {
    if (values[key] !== '') {
      url.searchParams.set(key, values[key] as string);
    } else {
      url.searchParams.delete(key);
    }
  });

  return url.href;
};

export const getUrlWithoutQueryParams = (keys: string[], baseUrl?: string) => {
  const urlToParse = baseUrl || getCurrentUrl();
  if (!urlToParse) {
    return '';
  }
  const url = new URL(urlToParse);

  keys.forEach(key => {
    url.searchParams.delete(key);
  });

  return url.href;
};

export const getQueryParamsString = (params: Record<string, string>) =>
  new URLSearchParams(params).toString();

export const updateUrl = (url: string) => {
  if (window && window.history) {
    window.history.replaceState(null, '', url);
  }
};

export const updateUrlAndEmitEvent = (url: string) => {
  updateUrl(url);
  eventBus.emit(EventBusEvent.UrlUpdate, { url });
};

export const getProductsListPath = (url: string) => {
  const hash = `#${RESULTS_HEADING_ID}`;

  try {
    const parsed = new URL(url);
    const path = parsed.pathname + parsed.search;

    return parsed.hash ? parsed.pathname + parsed.search + parsed.hash : path + hash;
  } catch {
    return url.includes('#') ? url : url + hash;
  }
};

export const resolveCategoryUrl = (urlPath: string, redirectUrl = '') => redirectUrl || urlPath;

export const getRedirect = (redirectUrl: string) => () => {
  window.location.href = redirectUrl;
};

export const isCurrentUrl = (url: string) =>
  url.endsWith(window.location.pathname + window.location.search) || url === window.location.href;
